/**
 * OPS-KPI-1〜3 推移チャート（Phase 3 / issue #197）。
 *
 * 課金社数 / MRR / 月次チャーン率 の 3 つを 12 ヶ月推移で表示。
 * 目標値（OPS-KPI-9）はゴーストライン（破線）として同チャートに重ねる。
 */
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { KpiOverviewResponse } from '../../lib/kpi-api';

interface MergedRow {
  month: string;
  actual: number | null;
  target: number | null;
}

function mergeSeries(
  overview: KpiOverviewResponse,
  kpiName: 'paid_workspace_count' | 'mrr' | 'monthly_churn_rate',
  pick: (p: KpiOverviewResponse['series'][number]) => number | null,
): MergedRow[] {
  const targetMap = new Map<string, number | null>();
  for (const t of overview.targets) {
    if (t.kpiName === kpiName) targetMap.set(t.month, t.targetValue);
  }
  return overview.series.map((p) => ({
    month: p.month,
    actual: pick(p),
    target: targetMap.get(p.month) ?? null,
  }));
}

function formatYen(n: number): string {
  return `¥${n.toLocaleString('ja-JP')}`;
}
function formatPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function KpiOverviewChart({
  overview,
  paidWorkspaceLabel,
  mrrLabel,
}: {
  overview: KpiOverviewResponse;
  paidWorkspaceLabel: string;
  mrrLabel: string;
}) {
  const paidSeries = mergeSeries(overview, 'paid_workspace_count', (p) => p.paidWorkspaceCount);
  const mrrSeries = mergeSeries(overview, 'mrr', (p) => p.mrr);
  const churnSeries = mergeSeries(overview, 'monthly_churn_rate', (p) => p.monthlyChurnRate);

  return (
    <div className="space-y-8">
      <ChartCard
        title={paidWorkspaceLabel}
        description="OPS-KPI-1。直近 12 ヶ月の月次課金社数（active + past_due）。ゴーストラインは標準シナリオ目標。"
        data={paidSeries}
        actualName={paidWorkspaceLabel}
        targetName="目標"
        valueFormatter={(v) => `${v}`}
      />
      <ChartCard
        title={`${mrrLabel}（円）`}
        description="OPS-KPI-2。月次経常収益（active + past_due の plan.monthly_price_jpy 合計）。"
        data={mrrSeries}
        actualName={mrrLabel}
        targetName="目標"
        valueFormatter={formatYen}
      />
      <ChartCard
        title="月次チャーン率"
        description="OPS-KPI-3。当月 canceled 数 ÷ 前月末 active 数。サンプル不足の月は描画しない。"
        data={churnSeries}
        actualName="月次チャーン率"
        targetName="目標"
        valueFormatter={(v) => formatPercent(v)}
      />
    </div>
  );
}

function ChartCard({
  title,
  description,
  data,
  actualName,
  targetName,
  valueFormatter,
}: {
  title: string;
  description: string;
  data: MergedRow[];
  actualName: string;
  targetName: string;
  valueFormatter: (n: number) => string;
}) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-5">
      <header className="mb-3">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <p className="mt-1 text-xs text-slate-500">{description}</p>
      </header>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 12 }} />
            <YAxis
              stroke="#64748b"
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => valueFormatter(Number(v))}
              width={80}
            />
            <Tooltip
              formatter={(value) => {
                if (value == null) return '—';
                const n = Number(Array.isArray(value) ? value[0] : value);
                return Number.isFinite(n) ? valueFormatter(n) : '—';
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="actual"
              name={actualName}
              stroke="#0ea5e9"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="target"
              name={targetName}
              stroke="#94a3b8"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
